<template>
  <div class="container">
    <!-- 日 / 週 / 月 選択エリア
      期間選択エリアの表示切替のトリガーです。 -->
    <div>
      <md-radio
        v-for="term in termTypes"
        :key="term.id"
        v-model="_termType"
        :value="term.id"
        >{{ term.label }}</md-radio
      >
    </div>
    <!-- 期間選択エリア -->
    <div>
      <transition mode="out-in">
        <!-- 週の選択 -->
        <div class="term-input" v-if="isWeekMode" key="w">
          <gl-input-date
            v-if="weekCountFrom"
            :min="minDate"
            :max="maxDate"
            :value.sync="_WeekFrom"
          />
          <gl-input-date
            v-if="weekCountTo"
            :min="minDate"
            :max="maxDate"
            :value.sync="_WeekTo"
          />
          <md-field class="divider">
            <md-select v-model="_weekType">
              <md-option
                v-for="week in weekTypes"
                :value="week.id"
                :key="week.id"
              >
                {{ week.label }}
              </md-option>
            </md-select>
          </md-field>
          <md-field class="week-count">
            <md-input
              v-model.number="weekCount"
              type="number"
              :min="1"
              :max="maxWeek"
              required
            ></md-input>
          </md-field>
          <div class="divider">
            <span>週間</span>
          </div>
        </div>
        <!-- 日の選択 -->
        <div class="term-input" v-if="isDateMode" key="d">
          <gl-input-date
            :min="minDate"
            :max="maxDate"
            :value.sync="_dateFrom"
          />
          <div class="divider">～</div>
          <gl-input-date :min="minDate" :max="maxDate" :value.sync="_dateTo" />
        </div>
        <!-- 月の選択 -->
        <div class="term-input" v-if="isMonthMode" key="m">
          <gl-select-month
            :min="minDate"
            :max="maxDate"
            :value.sync="monthFrom"
          />
          <div class="divider">～</div>
          <gl-select-month
            :min="minDate"
            :max="maxDate"
            :value.sync="monthTo"
          />
        </div>
        <!-- 日・曜日選択 -->
        <div v-if="isSelectMode" key="select">
          <div class="term-input">
            <gl-input-date
              :min="minDate"
              :max="maxDate"
              :value.sync="_dateFrom"
            />
            <div class="divider">～</div>
            <gl-input-date
              :min="minDate"
              :max="maxDate"
              :value.sync="_dateTo"
            />
          </div>
          <div>
            <md-radio v-model="_selectType" value="date">日付</md-radio>
            <md-radio v-model="_selectType" value="day">曜日</md-radio>
          </div>
          <div v-if="selectTypeIsDate">
            <div class="term-input">
              <!-- 日付選択 -->
              <md-field class="divider">
                <md-select v-model="_selectedDates" multiple>
                  <md-option
                    v-for="date in selectedDateOptions"
                    :value="date.id"
                    :key="date.id"
                  >
                    {{ date.label }}
                  </md-option>
                </md-select>
              </md-field>
              <gl-button
                @click="$emit('update:selectedDates', [])"
                class="clear-button"
                >Clear All</gl-button
              >
            </div>
            <div class="chips">
              <transition-group name="chips" tag="div">
                <gl-chip
                  v-for="d in selectedDatesForChips"
                  :key="d.id"
                  @delete="deleteDateItem(d.id)"
                  primary
                  deletable
                  >{{ d.label }}</gl-chip
                >
              </transition-group>
            </div>
          </div>
          <div v-else>
            <div class="term-input">
              <!-- 曜日選択 -->
              <md-field class="divider">
                <md-select v-model="_selectedDays" multiple>
                  <md-option
                    v-for="day in selectedDayOptions"
                    :value="day.id"
                    :key="day.id"
                  >
                    {{ day.label }}
                  </md-option>
                </md-select>
              </md-field>
              <gl-button
                @click="$emit('update:selectedDays', [])"
                class="clear-button"
                >Clear All</gl-button
              >
            </div>
            <transition-group name="chips" tag="div">
              <gl-chip
                v-for="d in selectedDaysForChips"
                :key="d.id"
                @delete="deleteDayItem(d.id)"
                primary
                deletable
                >{{ d.label }}</gl-chip
              >
            </transition-group>
          </div>
        </div>
      </transition>
    </div>
    <md-snackbar
      md-position="center"
      :md-duration="snackbar.duration"
      :md-active.sync="snackbar.show"
      md-persistent
    >
      {{ snackbar.message }}
    </md-snackbar>
  </div>
</template>
<script>
import moment from 'moment'
import GlSelectMonth from '@/components/common/GlSelectMonth.vue'
import GlInputDate from '@/components/common/GlInputDate.vue'
import GlChip from '@/components/common/GlChip.vue'
import {
  getEndDateByWeekCount,
  getStartDateByWeekCount,
  dateLimit,
  weekMove,
  toBasicDateFormat,
} from '@/util/date'
import {
  SELECTED_DATE_OPTIONS,
  SELECTED_DAY_OPTIONS,
} from '@/const/termSelectOptions'
import ErrorReport from '@/mixins/ErrorReport'
import QueryFilterRestoreMixin from '@/mixins/QueryFilterRestoreMixin'

/**
 * 期間選択コンポーネント
 * 内部的には開始、終了日だけを保持し、週や月の値は現在選択されている日付
 * から算出して取得・設定します。
 * なので、選択方式に週や月を選んでいてもこのコンポーネントから取得できるのは "日" です。
 */
export default {
  mixins: [ErrorReport, QueryFilterRestoreMixin],
  data() {
    return {
      termTypes: [
        { id: 'w', label: '週' },
        { id: 'm', label: '月' },
        { id: 'd', label: '日' },
        { id: 'select', label: '日・曜日選択' },
      ],
      weekTypes: [
        { id: 'COUNT_FROM', label: 'から' },
        { id: 'COUNT_TO', label: 'まで' },
      ],
      selectedDateOptions: SELECTED_DATE_OPTIONS,
      // BigQueryに合わせて日曜日:1～土曜日:7とする。
      selectedDayOptions: SELECTED_DAY_OPTIONS,
      // Snackbarの表示制御用のデータ
      snackbar: {
        show: false,
        duration: 10000,
        message: '',
      },
    }
  },
  components: { GlSelectMonth, GlInputDate, GlChip },
  props: {
    // 選択している選択種別（日 / 週 / 月）
    termType: {
      type: String,
      default: 'w',
    },
    dateFrom: {
      type: String,
      validator(value) {
        const regex = new RegExp(/^\d{8}$/g)
        return regex.test(value)
      },
    },
    dateTo: {
      type: String,
      validator(value) {
        const regex = new RegExp(/^\d{8}$/g)
        return regex.test(value)
      },
    },
    defaultWeekCount: {
      type: Number,
      default: 13,
    },
    // 選択している選択種別（から / まで）
    weekType: {
      type: String,
      default: 'COUNT_FROM',
    },
    // 日付選択か曜日選択
    selectType: {
      type: String,
      default: 'date',
    },
    // 選択した日付のリスト
    selectedDates: {
      type: Array,
      default: () => [],
    },
    // 選択した曜日のリスト
    selectedDays: {
      type: Array,
      default: () => [],
    },
  },
  watch: {
    /**
     * 最大日付の変更時、現在の日付値が上限を超えていないか確認し、
     * 超えている場合は新しい上限に自動調整します。
     * 予約機能による最大日付の変更など、ユーザーの操作以外で最大日付が変更されるケースに対応するために用意しています。
     */
    maxDate: {
      // immediate: true,
      handler() {
        this.adjustRangeIfNeeded()
      },
    },
    minDate: {
      // immediate: true,
      handler() {
        this.adjustRangeIfNeeded()
      },
    },
    /**
     * 選択種別の変更時、現在設定されている開始・終了日から、変更先の種別で適切となる
     * 開始・終了日に変換します。
     *
     * 例）"日" ⇒ "月" に変更した場合、"開始日" は月初の日付になり、"終了日" は月末の日付になります。
     *     2019/01/14～2019/05/03 -> 2019/01(2019/01/01)～2019/05(2019/05/31)
     */
    termType(value) {
      this.$emit('update:termType', value)
      let dateFrom = this.dateFrom
      let dateTo = this.dateTo
      // dateFrom, dateToはpropでもらい、親コンポーネントと.syncするので、
      // `update:dateFrom`, `update:dateTo` をemitするだけです。
      switch (value) {
        case 'w':
          // 週の場合終了日は表示されませんが、内部的には開始日からx週間後の日付を保持します。
          dateTo = getEndDateByWeekCount(dateFrom, this.weekCount)
          dateTo = dateLimit(dateTo, this.minDate, this.maxDate)

          this.$emit('update:dateTo', dateTo)
          break
        case 'm':
          dateFrom = moment(dateFrom)
            .startOf('month')
            .format('YYYYMMDD')
          dateTo = moment(dateTo)
            .endOf('month')
            .format('YYYYMMDD')
          dateFrom = dateLimit(dateFrom, this.minDate, this.maxDate)
          dateTo = dateLimit(dateTo, this.minDate, this.maxDate)
          this.$emit('update:dateFrom', dateFrom)
          this.$emit('update:dateTo', dateTo)
          break
        default:
          break
      }
    },
  },
  computed: {
    minDate() {
      return this.$store.getters.calendarEffectiveMinDate
    },
    maxDate() {
      return this.$store.getters.calendarEffectiveMaxDate
    },
    /**
     *ラジオボタンで押された期間のtermTypeを変更します
     */
    _termType: {
      get() {
        if (!this.termTypes.some(m => m.id === this.termType)) {
          this.$emit('update:termType', this.termTypes[0].id || 'd')
        }
        return this.termType
      },
      set(newVal) {
        this.$emit('update:termType', newVal)
      },
    },
    isDateMode() {
      return this.termType === 'd'
    },
    isWeekMode() {
      return this.termType === 'w'
    },
    isMonthMode() {
      return this.termType === 'm'
    },
    isSelectMode() {
      return this.termType === 'select'
    },
    maxWeek() {
      if (this.weekType === 'COUNT_FROM') {
        // から
        return moment(this.maxDate).diff(this.dateFrom, 'weeks') + 1
      } else {
        // まで
        return moment(this.dateTo).diff(this.minDate, 'weeks') + 1
      }
    },
    weekCountFrom() {
      return this.weekType === 'COUNT_FROM'
    },
    weekCountTo() {
      return this.weekType === 'COUNT_TO'
    },
    /**
     * termTypeが週で開始日が変更された場合ここで変更保存をしています。
     * x週間の間隔を保つため終了日も一緒に変更保存。
     */
    _WeekFrom: {
      get() {
        return this.dateFrom
      },
      set(newVal) {
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        let dateTo = weekMove(this.dateTo, this.dateFrom, newVal)
        dateTo = dateLimit(dateTo, this.minDate, this.maxDate)
        this.$emit('update:dateFrom', newVal)
        this.$emit('update:dateTo', dateTo)
      },
    },
    _WeekTo: {
      get() {
        return this.dateTo
      },
      set(newVal) {
        let dateFrom = weekMove(this.dateFrom, this.dateTo, newVal)
        dateFrom = dateLimit(dateFrom, this.minDate, this.maxDate)
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        this.$emit('update:dateFrom', dateFrom)
        this.$emit('update:dateTo', newVal)
      },
    },
    /**
     * termTypeが週で週数が変更された場合ここで変更保存をしています
     * 内部的に日付で期間を管理している為、週数と終了日をemitします
     * weekType（から、まで）によって処理を変えている
     */
    weekCount: {
      get() {
        return moment(this.dateTo).diff(moment(this.dateFrom), 'weeks') + 1
      },
      set(newVal) {
        if (this.weekCountFrom) {
          let dateTo = getEndDateByWeekCount(this.dateFrom, newVal)
          dateTo = dateLimit(dateTo, this.minDate, this.maxDate)
          this.$emit('update:dateTo', dateTo)
        } else if (this.weekCountTo) {
          let dateTo = this.dateTo
          newVal = getStartDateByWeekCount(dateTo, newVal)
          newVal = dateLimit(newVal, this.minDate, this.maxDate)
          this.$emit('update:dateFrom', newVal)
        }
      },
    },
    // このコンポーネントの入出力となる開始・終了日のフォーマットと、
    // inputに設定する値の日付フォーマットが異なるので変換用computedプロパティを用意
    /**
     * termTypeが日
     */
    _dateFrom: {
      get() {
        return this.dateFrom
      },
      set(newVal) {
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        this.$emit('update:dateFrom', newVal)
      },
    },
    /**
     * termTypeが日
     */
    _dateTo: {
      get() {
        return this.dateTo
      },
      set(newVal) {
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        this.$emit('update:dateTo', newVal)
      },
    },
    /**
     * termTypeが月
     */
    monthFrom: {
      get() {
        return moment(this.dateFrom).format('YYYYMM')
      },
      set(newVal) {
        newVal = toBasicDateFormat(newVal)
        newVal = moment(newVal)
          .startOf('month')
          .format('YYYYMMDD')
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        this.$emit('update:dateFrom', newVal)
      },
    },
    /**
     * termTypeが月
     */
    monthTo: {
      get() {
        return moment(this.dateTo).format('YYYYMM')
      },
      set(newVal) {
        newVal = toBasicDateFormat(newVal)
        newVal = moment(newVal)
          .endOf('month')
          .format('YYYYMMDD')
        newVal = dateLimit(newVal, this.minDate, this.maxDate)
        this.$emit('update:dateTo', newVal)
      },
    },
    /**
     * ～から、～までの選択肢が変更された際weekTypeを変更します
     */
    _weekType: {
      get() {
        return this.weekType
      },
      set(newVal) {
        this.$emit('update:weekType', newVal)
      },
    },
    /**
     * selectTypeが日付指定の場合True（日・曜日選択で使用）
     */
    selectTypeIsDate() {
      return this.selectType === 'date'
    },
    /**
     * 日付・曜日の選択肢が変更された際selectTypeを変更します
     */
    _selectType: {
      get() {
        return this.selectType
      },
      set(newVal) {
        this.$emit('update:selectType', newVal)
      },
    },
    /**
     * 選択している日付
     */
    _selectedDates: {
      get() {
        return this.selectedDates
      },
      set(newVal) {
        this.$emit('update:selectedDates', newVal)
      },
    },
    /**
     * 選択している日付（Chip用）
     */
    selectedDatesForChips: {
      get() {
        return [...this.selectedDateOptions]
          .filter(f => this._selectedDates.some(s => s === f.id))
          .sort((a, b) => a.id - b.id)
      },
      set(newVal) {
        this.$emit('update:selectedDates', newVal)
      },
    },
    /**
     * 選択している曜日
     */
    _selectedDays: {
      get() {
        return this.selectedDays
      },
      set(newVal) {
        this.$emit('update:selectedDays', newVal)
      },
    },
    /**
     * 選択している曜日（Chip用）
     */
    selectedDaysForChips: {
      get() {
        return [...this.selectedDayOptions]
          .filter(f => this._selectedDays.some(s => s === f.id))
          .sort((a, b) => a.order - b.order)
      },
      set(newVal) {
        this.$emit('update:selectedDays', newVal)
      },
    },
    errors() {
      const errors = []
      if (this.dateFrom && this.dateTo) {
        if (this.dateFrom > this.dateTo) {
          errors.push('終了期間は開始期間以降を選択してください')
        }
      } else {
        errors.push('期間を選択してください')
      }
      // 日・曜日選択で選択している日付または曜日が無い場合
      if (this.isSelectMode) {
        if (this.selectTypeIsDate) {
          if (!this._selectedDates || this._selectedDates.length === 0) {
            errors.push('対象日付を1つ以上選択してください')
          }
        } else {
          if (!this._selectedDays || this._selectedDays.length === 0) {
            errors.push('対象曜日を1つ以上選択してください')
          }
        }
      }
      return errors
    },
  },
  methods: {
    adjustRangeIfNeeded() {
      // QueryFilterRestoreMixinのcomputedを参照
      if (this._queryFilterRestoring) return

      const adjustedFrom = dateLimit(this.dateFrom, this.minDate, this.maxDate)
      const adjustedTo = dateLimit(this.dateTo, this.minDate, this.maxDate)

      if (adjustedFrom === this.dateFrom && adjustedTo === this.dateTo) {
        return
      }

      this.$emit('update:dateFrom', adjustedFrom)
      this.$emit('update:dateTo', adjustedTo)
      this.snackbar.message =
        '指定期間が選択可能な期間外だったため、自動調整しました。'
      this.snackbar.show = true
    },
    // QueryFilterRestoreMixinのmethodsを上書き
    // 分析条件の復元が完了したときの処理
    onQueryFilterRestoreCompleted() {
      this.adjustRangeIfNeeded()
    },
    deleteDateItem(id) {
      this._selectedDates = this._selectedDates.filter(f => f !== id)
    },
    deleteDayItem(id) {
      this._selectedDays = this._selectedDays.filter(f => f !== id)
    },
  },
  /**
   * APIから選択出来る期限のカレンダーを取得します
   */
  async mounted() {
    // 検索期間の初期値指定、任意の初期値がある場合はelseへ
    // 検索期限は外部APIから取得する必要がある為、ここに記述
    let dateFrom = this.dateFrom
    let dateTo = this.dateTo
    if (!dateFrom || !dateTo) {
      dateFrom = moment(this.maxDate)
        .subtract(this.defaultWeekCount, 'week')
        .add(1, 'days')
        .format('YYYYMMDD')
      dateTo = toBasicDateFormat(this.maxDate)
      // 検索期間が検索期限を超えていた場合の処理
      dateFrom = dateLimit(dateFrom, this.minDate, this.maxDate)
      dateTo = dateLimit(dateTo, this.minDate, this.maxDate)
      this.$emit('update:dateFrom', dateFrom)
      this.$emit('update:dateTo', dateTo)
    }
  },
}
</script>

<style lang="scss" scoped>
.container {
  overflow: hidden;
}

.term-input {
  display: flex;
}

.week-count {
  width: 70px;
}

.clear-button {
  margin-top: 16px;
}

.divider {
  align-items: center;
  display: flex;
  justify-content: center;
  margin-left: 16px;
  margin-right: 16px;
  white-space: nowrap;
  width: 70px;
}
// 期間選択エリアの切替アニメーション
.v-enter-active,
.v-leave-active {
  transition: transform 0.1s ease-in-out, opacity 0.1s ease-in-out;
}
.v-enter,
.v-leave-to {
  opacity: 0;
}
.v-enter {
  transform: translateX(-50%);
}
.v-leave-to {
  transform: translateX(50%);
}

.gl-chip {
  margin-left: var(--spacing-small);
  margin-top: var(--spacing-small);
  // For Transition Animation
  display: inline-block;
}

// Chips Animation
.chips-enter,
.chips-leave-to {
  opacity: 0;
}
.chips-leave-active {
  position: absolute;
}
.chips-move {
  transition: transform 0.3s;
}
</style>
